import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { request, useOperations } from './OperationsProvider';

// Places the plant has not been to yet, found on the map rather than typed from memory.
//
// Asked of Google through this product's own server rather than of OpenStreetMap from the browser.
// OpenStreetMap is free and knows Indian industrial addresses poorly - a works every driver can
// name is often not in it - and Google will not answer a browser directly, so the key stays on the
// server and the page asks the server.
//
// Either way it waits for a pause in the typing and only once there is enough of a name to be worth
// looking up, because each of these costs the deployment money.
const searchPlaces = async (text, signal) => {
  const found = await request(`/api/operations/places?q=${encodeURIComponent(text)}`, 'GET', null, signal);
  return (found || []).map((place) => ({ id: place.id, name: place.name, full: place.full }));
};

// Where a chosen place actually is. Google charges for the position separately from the suggestion,
// so it is asked for once, for the one place the desk picked.
const locatePlace = async (place) => {
  if (!place?.id) return place;
  try {
    const at = await request(`/api/operations/places/${encodeURIComponent(place.id)}`, 'GET');
    return { ...place, latitude: at.latitude, longitude: at.longitude, full: at.full || place.full };
  } catch { return place; }
};

// A field that offers what the plant has used before, and still takes anything else.
//
// A dropdown alone is wrong at a counter: the one lorry that matters is always carrying the
// material nobody has entered yet, and a list that cannot be escaped turns into somebody choosing
// the nearest wrong option. So the list is a suggestion — type to filter it, or type straight past
// it — and whatever is typed joins the list for next time, which is why nobody has to maintain it.
//
// `onPlace` turns the field into a destination: the plant's own places first, and below them
// whatever the map knows, so a place nobody has hauled to yet arrives with its coordinates and the
// road to it can be drawn without anybody looking it up.
const CatalogField = ({
  list, label, value, onChange, onPlace, required = true, disabled = false, helperText,
}) => {
  const { data } = useOperations();
  const [found, setFound] = useState([]);
  const [looking, setLooking] = useState(false);
  // Transporters are a register in their own right, with contacts and GSTIN; the rest are words
  // the plant uses. Both read the same way here.
  const options = list === 'transporter'
    ? [...new Set([
      ...(data.transporters || []).map((t) => t.name),
      ...(data.catalog || []).filter((c) => c.list === 'transporter').map((c) => c.name),
    ])]
    : (data.catalog || []).filter((c) => c.list === list).map((c) => c.name);
  const known = useMemo(() => [...options].sort((a, b) => a.localeCompare(b)), [options.join('')]);
  const typed = String(value || '').trim();
  useEffect(() => {
    if (!onPlace || typed.length < 4 || known.some((name) => name.toLowerCase() === typed.toLowerCase())) { setFound([]); return undefined; }
    const abort = new AbortController();
    const wait = setTimeout(async () => {
      setLooking(true);
      try { setFound(await searchPlaces(typed, abort.signal)); } catch (e) { if (e.name !== 'AbortError') setFound([]); } finally { setLooking(false); }
    }, 700);
    return () => { clearTimeout(wait); abort.abort(); };
  }, [typed, !!onPlace]);
  // A place already on the plant's own list wins: it is the spelling the plant uses, and it already
  // carries wherever it was found to be the first time.
  const places = found.filter((place) => !known.some((name) => name.toLowerCase() === place.name.toLowerCase()));
  const choose = async (chosen) => {
    const place = places.find((p) => p.name === chosen);
    onChange(chosen || '');
    if (!onPlace) return;
    // A place off the map is located now that one has actually been chosen; anything already on the
    // plant's own list carries wherever it was found to be the first time.
    onPlace(place ? await locatePlace(place) : (data.catalog || []).find((c) => c.list === list && c.name === chosen) || null);
  };

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      disabled={disabled}
      filterOptions={(all) => all}
      options={[...known.filter((name) => name.toLowerCase().includes(typed.toLowerCase())), ...places.map((p) => p.name)]}
      value={value || ''}
      onChange={(event, chosen) => choose(chosen || '')}
      onInputChange={(event, text, reason) => { if (reason === 'input') onChange(text); }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText ?? (looking ? 'Looking this place up on the map…' : known.length ? 'Choose one, or type a new one' : 'Type it; it will be offered next time')}
          inputProps={{ ...params.inputProps, maxLength: 120 }}
        />
      )}
    />
  );
};

export default CatalogField;
